
import { Recipe } from "@/types/recipe";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { formatDuration } from "@/utils/timeUtils";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, CircleDot, Bot } from "lucide-react";
import RecipeIcon from "@/components/common/RecipeIcon";
import RecipeImage from "@/components/recipe/RecipeImage";

interface RecipeCardProps {
  recipe: Recipe & {
    usageCount?: number;
    lastUsedDate?: string | null;
    daysSinceLastUse?: number | null;
  };
  onDelete: (id: string) => void;
  onTagSelect?: (tag: string) => void;
}

const RecipeCard = ({ recipe, onTagSelect }: RecipeCardProps) => {
  const navigate = useNavigate();

  const getUsageBadgeVariant = () => {
    if (!recipe.usageCount || recipe.usageCount === 0) return "secondary";
    if (recipe.usageCount >= 3) return "default";
    return "outline";
  };

  const handleView = () => {
    navigate(`/recipe/${recipe.id}`);
  };

  // Create unified tags array
  const allTags = [];

  // Add quick meal tag
  if (recipe.isQuickRecipe) {
    allTags.push({
      type: 'quick-meal',
      content: (
        <Badge key="quick-meal" variant="outline" className="text-xs border-emerald-300 text-emerald-700 bg-emerald-50">
          <RecipeIcon type="quick-recipe" size={10} className="mr-1" />
          Quick meal
        </Badge>
      )
    });
  }

  // Add AI suggestion tag
  if (recipe.suggestion_id) {
    allTags.push({
      type: 'ai-suggestion',
      content: (
        <Badge key="ai-suggestion" variant="outline" className="text-xs border-violet-300 text-violet-700 bg-violet-50">
          <Bot className="h-3 w-3 mr-1" />
          AI-suggestion
        </Badge>
      )
    });
  }

  // Add usage statistics tag
  if (recipe.usageCount !== undefined) {
    if (recipe.usageCount > 0) {
      allTags.push({
        type: 'usage',
        content: (
          <Badge key="usage" variant={getUsageBadgeVariant()} className="text-xs">
            <TrendingUp className="h-3 w-3 mr-1" />
            Used {recipe.usageCount}x
          </Badge>
        )
      });
    } else {
      allTags.push({
        type: 'usage',
        content: (
          <Badge key="never-used" variant="outline" className="text-xs border-orange-300 text-orange-700 bg-orange-50">
            <CircleDot className="h-3 w-3 mr-1" />
            Never used
          </Badge>
        )
      });
    }
  }

  // Add regular AI tags
  if (recipe.tags && recipe.tags.length > 0) {
    recipe.tags.forEach((tag, index) => {
      allTags.push({
        type: 'regular',
        content: (
          <Badge 
            key={`tag-${index}`} 
            variant="secondary" 
            className="text-xs bg-golden-light text-charcoal border-golden cursor-pointer hover:bg-golden hover:text-cream transition-colors"
            onClick={() => onTagSelect?.(tag)}
          >
            {tag}
          </Badge>
        )
      });
    });
  }
  
  return (
    <Card className="h-full flex flex-col border-golden-light bg-card shadow-sm hover:shadow-md transition-shadow duration-200 hover:border-sage/30">
      <div className="p-3 pb-0">
        <RecipeImage 
          recipe={recipe}
          size="sm"
          className="w-full mb-3"
        />
      </div>
      <CardHeader className="pb-2 pt-0">
        <CardTitle className="line-clamp-2 text-base md:text-lg font-playfair text-charcoal">
          <button 
            onClick={handleView}
            className="flex items-center gap-2 text-left hover:text-sage transition-colors cursor-pointer"
          >
            {recipe.isQuickRecipe && (
              <RecipeIcon type="quick-recipe" size={14} />
            )}
            {recipe.title}
          </button>
        </CardTitle>
        {!recipe.isQuickRecipe && (formatDuration(recipe.prepTime, false) || formatDuration(recipe.cookTime, false)) && (
          <div className="text-xs md:text-sm text-charcoal/70 font-geist">
            <span className="mr-3">Prep: {formatDuration(recipe.prepTime, true)}</span>
            <span>Cook: {formatDuration(recipe.cookTime, true)}</span>
          </div>
        )}
      </CardHeader>
      <CardContent className="flex-1">
        <p className="text-sm text-muted-foreground line-clamp-3 font-geist">
          {recipe.description || 'No description available.'}
        </p>
        
        {/* Unified Tags Display */}
        {allTags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {allTags.map((tag, index) => (
              <div key={index}>
                {tag.content}
              </div>
            ))}
          </div>
        )}
      </CardContent>
      <CardFooter className="pt-2">
        <Button 
          variant="outline" 
          size="sm" 
          className="w-full h-9 md:h-8 text-sm font-geist border-sage text-sage hover:bg-sage hover:text-cream" 
          onClick={handleView}
        >
          View Recipe
        </Button>
      </CardFooter>
    </Card>
  );
};

export default RecipeCard;
